import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import { Button } from "../ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import MeusDocumentosPiloto from "./MeusDocumentosPiloto";
import DocumentoAcoes from "./DocumentoAcoes";

// Blocos da ficha de piloto no ecrã do aluno. Fechados por defeito: mostram só o resumo mínimo.
const DUAS_HORAS = 2 * 3600000;

const hm = (min) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, "0")}`;
const pad = (n) => String(n).padStart(2, "0");
const diaUtc = (iso) => new Date(iso).toISOString().slice(0, 10);
const hojeUtc = () => new Date().toISOString().slice(0, 10);
const amanhaUtc = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10);
const quandoCurto = (iso) => {
  const d = diaUtc(iso);
  const dia = d === hojeUtc() ? "hoje" : d === amanhaUtc() ? "amanhã" : `${d.slice(8, 10)}/${d.slice(5, 7)}`;
  return `${dia}, ${pad(new Date(iso).getUTCHours())}:00 UTC`;
};
const quandoLongo = (iso) => {
  const h = new Date(iso).getUTCHours();
  return `${diaUtc(iso).slice(8, 10)}/${diaUtc(iso).slice(5, 7)} · ${pad(h)}:00–${pad((h + 1) % 24)}:00 UTC`;
};
const erro = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

function Cabecalho({ titulo, resumo, aberto, onToggle, testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={aberto}
      className="w-full text-left flex items-center justify-between gap-3"
      data-testid={testId}
    >
      <div className="min-w-0">
        <div className="font-semibold text-slate-100">{titulo}</div>
        <div className="text-xs text-slate-400 break-words">{resumo}</div>
      </div>
      {aberto
        ? <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
        : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />}
    </button>
  );
}

export function ProximosVoos() {
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const [reservas, setReservas] = useState(null);

  const carregar = async () => {
    try {
      const { data } = await axios.get(`${API}/di/reservations/mine`, { withCredentials: true });
      const agora = Date.now();
      setReservas((data || []).filter((r) => r.status === "active" && Date.parse(r.ends_at) > agora));
    } catch {
      setReservas([]);
    }
  };

  useEffect(() => { carregar(); }, []);

  if (reservas === null) return null;
  const proxima = reservas[0];
  const resumo = proxima
    ? `${reservas.length} ${reservas.length === 1 ? "reserva" : "reservas"} · a próxima: ${quandoCurto(proxima.starts_at)}`
    : "Sem reservas · reserva um horário livre";

  const cancelar = async (id) => {
    try {
      await axios.post(`${API}/di/reservations/${id}/cancel`, {}, { withCredentials: true });
      toast.success("Reserva cancelada.");
      carregar();
    } catch (e) {
      toast.error(erro(e, "Não foi possível cancelar."));
    }
  };

  return (
    <section className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 sm:p-4 mb-3" data-testid="student-proximos-voos">
      <Cabecalho titulo="Próximos voos" resumo={resumo} aberto={aberto} onToggle={() => setAberto(!aberto)} testId="proximos-voos-toggle" />
      {aberto && (
        <div className="mt-3 space-y-2 text-xs">
          {reservas.length === 0 && <p className="text-slate-400">Ainda não tens voos reservados.</p>}
          {reservas.map((r) => {
            const podeCancelar = Date.parse(r.starts_at) - Date.now() >= DUAS_HORAS;
            return (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 pt-2" data-testid="student-reserva">
                <span className="text-slate-200">
                  <span className="font-medium">{quandoLongo(r.starts_at)}</span>
                  <br />
                  <span className="text-slate-400 font-mono">{r.aircraft_registration}</span>
                  <span className="text-slate-400"> · {r.kind === "instrucao" ? `instrução com ${r.instructor_name || "instrutor"}` : r.kind === "passageiro" ? "passageiro" : "voo"}</span>
                </span>
                {podeCancelar
                  ? <Button variant="outline" size="sm" onClick={() => cancelar(r.id)}>Cancelar</Button>
                  : <span className="text-slate-500 text-[11px]">Cancelamento fechado</span>}
              </div>
            );
          })}
          <div className="flex justify-end pt-1">
            <Button size="sm" onClick={() => navigate("/reservas")} data-testid="student-reservar">Reservar voo</Button>
          </div>
        </div>
      )}
    </section>
  );
}

function Barra({ titulo, valor, alvo }) {
  const pct = alvo > 0 ? Math.min(100, Math.round((valor / alvo) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between gap-2 text-slate-200">
        <span>{titulo}</span>
        <span className="font-medium">{hm(valor)} de {hm(alvo)}</span>
      </div>
      <div className="h-2 rounded bg-slate-800 overflow-hidden mt-1" role="progressbar" aria-valuemin={0} aria-valuemax={alvo} aria-valuenow={valor}>
        <div className="h-full bg-orange-400/80" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function InstrucaoPratica() {
  const [aberto, setAberto] = useState(false);
  const [dados, setDados] = useState(null);

  useEffect(() => {
    axios.get(`${API}/di/pratica/me`, { withCredentials: true })
      .then((r) => setDados(r.data))
      .catch(() => setDados(null));
  }, []);

  if (!dados || dados.kind !== "student") return null;
  const resumo = `DC ${hm(dados.dc_minutes)} de ${hm(dados.dc_target_minutes)} · solo ${hm(dados.solo_minutes)} de ${hm(dados.solo_target_minutes)}`;

  return (
    <section className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 sm:p-4 mb-3" data-testid="student-instrucao-pratica">
      <Cabecalho titulo="Instrução prática" resumo={resumo} aberto={aberto} onToggle={() => setAberto(!aberto)} testId="instrucao-pratica-toggle" />
      {aberto && (
        <div className="mt-3 space-y-3 text-xs">
          <Barra titulo="Duplo comando" valor={dados.dc_minutes} alvo={dados.dc_target_minutes} />
          <Barra titulo="Horas a solo" valor={dados.solo_minutes} alvo={dados.solo_target_minutes} />
          <p className="text-slate-500">{dados.flights} {dados.flights === 1 ? "voo contado" : "voos contados"}, só em aeronaves ultraleve.</p>
        </div>
      )}
    </section>
  );
}

// Documentos e validades da própria pessoa, com o estado (expirado, urgente, a expirar, válido ou em falta).
const ORDEM_ESTADO = { expirado: 0, em_falta: 0, urgente: 1, atencao: 2, pendente: 2, ok: 3 };
const ESTADO_TXT = {
  expirado: { txt: "expirado", cls: "text-red-300" },
  em_falta: { txt: "em falta", cls: "text-red-300" },
  pendente: { txt: "aguarda aprovação", cls: "text-sky-300" },
  urgente: { txt: "urgente", cls: "text-amber-300" },
  atencao: { txt: "a expirar", cls: "text-sky-300" },
  ok: { txt: "válido", cls: "text-emerald-300" },
};
const dataPt = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const textoDias = (i) => {
  if (i.estado === "pendente") return "enviado pelo piloto";
  if (i.estado === "em_falta") return "sem data registada";
  if (i.dias_restantes < 0) return `expirou há ${-i.dias_restantes} ${-i.dias_restantes === 1 ? "dia" : "dias"}`;
  return `faltam ${i.dias_restantes} ${i.dias_restantes === 1 ? "dia" : "dias"}`;
};

export function DocumentosValidades() {
  const [aberto, setAberto] = useState(false);
  const [dados, setDados] = useState(null);

  // Recarrega a lista sem refresh da página: também depois de um envio, para o envio novo aparecer logo
  const carregarDados = useCallback(() => {
    axios.get(`${API}/di/documentos/me`, { withCredentials: true })
      .then((r) => setDados(r.data))
      .catch(() => setDados(null));
  }, []);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  if (!dados) return null;
  const itens = [...dados.itens].sort((a, b) => ORDEM_ESTADO[a.estado] - ORDEM_ESTADO[b.estado]);
  // Um envio por aprovar não é um alerta: já está tratado pelo piloto e está com o DI
  const alertas = itens.filter((i) => i.estado !== "ok" && i.estado !== "pendente");
  const resumo = alertas.length === 0
    ? "Todos válidos"
    : `${alertas.length} ${alertas.length === 1 ? "alerta" : "alertas"} · ${alertas[0].label}: ${textoDias(alertas[0])}`;

  return (
    <section className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 sm:p-4 mb-3" data-testid="student-documentos">
      <Cabecalho titulo="Documentos e validades" resumo={resumo} aberto={aberto} onToggle={() => setAberto(!aberto)} testId="documentos-toggle" />
      {aberto && (
        <div className="mt-3 space-y-2 text-xs">
          {itens.map((i) => (
            <div key={i.label} className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 pt-2" data-testid="student-documento">
              <span className="text-slate-200">{i.label}</span>
              <span className="text-right">
                <span className="text-slate-300">{dataPt(i.valid_until)}</span>
                <span className={`ml-2 ${ESTADO_TXT[i.estado].cls}`}>{ESTADO_TXT[i.estado].txt} · {textoDias(i)}</span>
                <DocumentoAcoes docId={i.doc_id} />
                {i.motivo_recusa && <span className="block text-red-200" data-testid="documento-motivo-recusa">Motivo: {i.motivo_recusa}</span>}
              </span>
            </div>
          ))}
          {dados.prova_com_examinador && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 pt-2">
              <span className="text-slate-200">Prova com examinador</span>
              <span className="text-slate-300">{dataPt(dados.prova_com_examinador)}</span>
            </div>
          )}
          {/* Pilotos, alunos e instrutores enviam documentos para aprovação do DI */}
          {(dados.kind === "pilot" || dados.kind === "instructor") && (
            <MeusDocumentosPiloto aluno={!!dados.aluno} instrutor={dados.kind === "instructor"} onEnviado={carregarDados} />
          )}
        </div>
      )}
    </section>
  );
}
